import FloorMetadataState from "./floor-metadata-state";
import UserImageData from "../../data/user-image-data";
import { VelavuSite } from "velavu-js-api";
import React, { useCallback, useMemo, useState } from "react";
import { FloorCreateStep } from "./floor-create-step";
import PlanToolType from "./plan-tool-type";
import ImagePositioning, {
	DEFAULT_IMAGE_POSITIONING,
	imagePositioningToEdgeCoords,
} from "./image-positioning";
import {
	FloorplanImage,
	floorplanImageFromFloor,
} from "../../data/floorplan-image";

export const DRAW_REFERENCE_FLOOR_ID_CURRENT = Symbol(
	"drawReferenceFloorIDCurrent",
);

export interface FloorCreateState {
	// The site currently being edited, or null if the editor is closed
	activeSite: VelavuSite | null;

	// The current step of the wizard
	step: FloorCreateStep;

	// The metadata of the floor being edited
	metadata: FloorMetadataState;

	// The image of the floor being edited, or null if no image is set
	floorImage: UserImageData | null;

	// The opacity of the floor image, ranging from 0 (transparent) to 1 (opaque)
	floorImageOpacity: number;

	// The floor plan image location state
	placeImageState: ImagePositioning;

	// The ID of the reference floor during the image placement process
	placeImageReferenceFloorID: string | null;

	// The currently selected drawing tool
	drawToolType: PlanToolType;

	// The walls being drawn, represented as an array of arrays of points
	drawWalls: [number, number][][];

	//The ID of the floor to use as a reference while drawing walls, or a special value indicating the current floor
	drawReferenceFloorID:
		string | typeof DRAW_REFERENCE_FLOOR_ID_CURRENT | null;

	// The reference image to display on the map
	referenceImage: FloorplanImage | null;
}

export interface FloorCreateActions {
	createNewFloor(site: VelavuSite): void;
	close(): void;

	setStep: React.Dispatch<React.SetStateAction<FloorCreateStep>>;
	setMetadata: React.Dispatch<React.SetStateAction<FloorMetadataState>>;
	setFloorImage: React.Dispatch<React.SetStateAction<UserImageData | null>>;
	setFloorImageOpacity: React.Dispatch<React.SetStateAction<number>>;
	setPlaceImageState: React.Dispatch<React.SetStateAction<ImagePositioning>>;
	setPlaceImageReferenceFloorID: React.Dispatch<
		React.SetStateAction<string | null>
	>;
	setDrawToolType: React.Dispatch<React.SetStateAction<PlanToolType>>;
	setDrawWalls: React.Dispatch<React.SetStateAction<[number, number][][]>>;
	setDrawReferenceFloorID: React.Dispatch<
		React.SetStateAction<
			string | typeof DRAW_REFERENCE_FLOOR_ID_CURRENT | null
		>
	>;
}

export default function useFloorCreateState(): [
	FloorCreateState,
	FloorCreateActions,
] {
	const [activeSite, setActiveSite] = useState<VelavuSite | null>(null);
	const [step, setStep] = useState<FloorCreateStep>(FloorCreateStep.Intro);
	const [metadata, setMetadata] = useState<FloorMetadataState>({
		level: 0,
		name: "",
	});
	const [floorImage, setFloorImage] = useState<UserImageData | null>(null);
	const [floorImageOpacity, setFloorImageOpacity] = useState<number>(0.5);
	const [placeImageState, setPlaceImageState] = useState<ImagePositioning>(
		DEFAULT_IMAGE_POSITIONING,
	);
	const [placeImageReferenceFloorID, setPlaceImageReferenceFloorID] =
		useState<string | null>(null);
	const [drawToolType, setDrawToolType] = useState<PlanToolType>("draw");
	const [drawWalls, setDrawWalls] = useState<[number, number][][]>([]);
	const [drawReferenceFloorID, setDrawReferenceFloorID] = useState<
		string | typeof DRAW_REFERENCE_FLOOR_ID_CURRENT | null
	>(null);

	const createNewFloor = useCallback((site: VelavuSite) => {
		setActiveSite(site);
		setStep(FloorCreateStep.Intro);
		setMetadata({ level: 0, name: "" });
		setFloorImage(null);
		setFloorImageOpacity(0.5);
		setPlaceImageState({
			center: site.coordinates,
			scale: 1,
			rotation: 0,
		});
		setPlaceImageReferenceFloorID(null);
		setDrawToolType("draw");
		setDrawWalls([]);
		setDrawReferenceFloorID(null);
	}, []);

	const close = useCallback(() => {
		setActiveSite(null);
	}, []);

	const referenceImage = useMemo((): FloorplanImage | null => {
		if (!activeSite) {
			return null;
		}

		function getFloorImage(): FloorplanImage | null {
			if (floorImage === null) {
				return null;
			}

			const coords = imagePositioningToEdgeCoords(
				placeImageState,
				floorImage.dimensions.width,
				floorImage.dimensions.height,
			);

			return {
				data: floorImage,
				coords: coords,
				opacity: floorImageOpacity,
			};
		}

		if (step === FloorCreateStep.Place) {
			if (typeof placeImageReferenceFloorID === "string") {
				//Show the selected floor's image
				const refFloor = activeSite.floors?.find(
					(f) => f.id === placeImageReferenceFloorID,
				);
				return floorplanImageFromFloor(refFloor);
			}
		} else if (step === FloorCreateStep.DrawPrompt) {
			//Always show the current floor's image on the prompt step
			return getFloorImage();
		} else if (step === FloorCreateStep.Draw) {
			if (drawReferenceFloorID === DRAW_REFERENCE_FLOOR_ID_CURRENT) {
				//Show the current floor's image
				return getFloorImage();
			} else if (typeof drawReferenceFloorID === "string") {
				//Show the selected floor's image
				const refFloor = activeSite.floors?.find(
					(f) => f.id === drawReferenceFloorID,
				);
				return floorplanImageFromFloor(refFloor);
			}
		}

		return null;
	}, [
		activeSite,
		step,
		placeImageReferenceFloorID,
		drawReferenceFloorID,
		floorImage,
		placeImageState,
		floorImageOpacity,
	]);

	const state = useMemo(
		(): FloorCreateState => ({
			activeSite,
			step,
			metadata,
			floorImage,
			floorImageOpacity,
			placeImageState,
			placeImageReferenceFloorID,
			drawToolType,
			drawWalls,
			drawReferenceFloorID,
			referenceImage,
		}),
		[
			activeSite,
			step,
			metadata,
			floorImage,
			floorImageOpacity,
			placeImageState,
			placeImageReferenceFloorID,
			drawToolType,
			drawWalls,
			drawReferenceFloorID,
			referenceImage,
		],
	);

	const actions = useMemo(
		(): FloorCreateActions => ({
			createNewFloor,
			close,
			setStep,
			setMetadata,
			setFloorImage,
			setFloorImageOpacity,
			setPlaceImageState,
			setPlaceImageReferenceFloorID,
			setDrawToolType,
			setDrawWalls,
			setDrawReferenceFloorID,
		}),
		[
			createNewFloor,
			close,
			setStep,
			setMetadata,
			setFloorImage,
			setFloorImageOpacity,
			setPlaceImageState,
			setPlaceImageReferenceFloorID,
			setDrawToolType,
			setDrawWalls,
			setDrawReferenceFloorID,
		],
	);

	return [state, actions];
}
